import { InputNumber as InputNumberAntd, type InputNumberProps } from "antd";
import { type FocusEvent, type KeyboardEvent, useState } from "react";
import { numberToString, stringToNumber } from "std/format";

const allowedKeys = [
	"a", // Pra poder dar CTRL + a para selecionar tudo
	"c", // Para poder dar CTRL + c para copiar
	"v", // Para poder dar CTRL + v para colar
	",", // Decimais
	".", // Decimais
	"-",
	"0",
	"1",
	"2",
	"3",
	"4",
	"5",
	"6",
	"7",
	"8",
	"9",
	"Backspace",
	"Delete",
	"Control",
	"Home",
	"End",
	"Tab",
	"ArrowLeft",
	"ArrowRight",
	"ArrowUp",
	"ArrowDown",
];

type InputPercentualProps = Omit<InputNumberProps, "onChange"> & {
	onChange?: (value: string | number | null) => void;
	allowNegative?: boolean;
	decimalScale?: number;
};

/** Input númerico. Serve para números inteiros e decimais */
export function InputPercentual({
	style = { width: "100%" },
	allowNegative = false,
	decimalScale = 2,
	addonBefore = "%",
	placeholder,
	onChange,
	onBlur,
	onKeyDown,
	...rest
}: InputPercentualProps) {
	const [isFocused, setIsFocused] = useState(false);

	const onFocus = (): void => {
		setIsFocused(true);
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
		setIsFocused(false);

		if (onBlur) {
			onBlur(e);
		}
	};

	const handleOnChange = (newValue: string | number | null): void => {
		if (onChange) {
			onChange(newValue);
		}
	};

	const placeholderInner = placeholder ? placeholder : decimalScale === 0 ? "0%" : numberToString(0, "decimal", decimalScale);

	const formatter = (value: Optional<string | number>): string => {
		if (!value) {
			return "";
		}

		// Se o input está com foco então retorna o valor sem formatar
		if (isFocused) {
			return value.toString().replace(".", ",");
		}

		const num = Number(value);

		if (Number.isNaN(num)) {
			return "";
		}

		return numberToString(num, "decimal", decimalScale);
	};

	const parser = (displayValue: string | undefined): string | number => {
		if (typeof displayValue !== "string" || displayValue === "") {
			return "";
		}

		if (displayValue === "-") {
			return displayValue;
		}

		return stringToNumber(displayValue);
	};

	const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
		if (onKeyDown) {
			onKeyDown(e);
		}

		// Se não deixa negativo não deixa digitar -
		if (!allowNegative && e.key === "-") {
			e.preventDefault();
		}

		// Se for apenas números inteiros não permite . ou ,
		if (decimalScale === 0 && (e.key === "." || e.key === ",")) {
			e.preventDefault();
		}

		// Verifica se a tecla pressionada é valida
		if (!allowedKeys.includes(e.key)) {
			e.preventDefault();
		}
	};

	return (
		<InputNumberAntd
			style={style}
			controls={false}
			decimalSeparator=","
			min={0}
			max={100}
			placeholder={placeholderInner}
			formatter={formatter}
			parser={parser}
			onChange={handleOnChange}
			onKeyDown={handleOnKeyDown}
			stringMode={true}
			addonBefore={addonBefore}
			onFocus={onFocus}
			onBlur={handleBlur}
			{...rest}
		/>
	);
}
